import React, { useCallback, useMemo, useState } from "react";
import { MoveFocusInside } from "react-focus-lock";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import useAcSelectDate from "~/components/Hooks/useAcSelectDate";
import { SUGGESTION } from "~/components/tasks/SuggestWizard/suggestionUtils";
import { OptionCard } from "~/pages/GettingStarted/OptionCard";
import { ProjectForm } from "~/pages/GettingStarted/ProjectForm";
import { ProjectPrompt } from "~/pages/GettingStarted/ProjectPrompt";
import { ProjectTemplates } from "~/pages/GettingStarted/ProjectTemplates";
import { getIsMultipleAssigneesEnabled } from "~/store/selectors/selector.configOptions";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import { getDefaultJobType } from "~/store/selectors/selector.jobTypes";
import { getLoggedUserId } from "~/store/selectors/selector.users";

export const LeftSideContent = ({ step, setStep, onClose, setFormState, isAlreadyCompleted, setProjectId }) => {
  const { t } = useTranslation();
  const createProjectFromPrompt = useSelector((state) => getFeatureFlag(state, `ai_enabled`));
  const defaultJobType = useSelector(getDefaultJobType);
  const loggedUserId = useSelector(getLoggedUserId);
  const { today } = useAcSelectDate();
  const [initialProjectFormData, setInitialProjectFormData] = useState([]);
  const multiAssigneesEnabled = useSelector(getIsMultipleAssigneesEnabled);
  const onTemplateClick = useCallback(
    (data) => {
      setStep("project-form-from-template");
      setInitialProjectFormData(data);
    },
    [setStep]
  );

  const createProjectOptions = useMemo(() => {
    let options = [];

    createProjectFromPrompt &&
      options.push({
        img: "/onboarding/images/UseAiIcon.png",
        title: t("Use AI"),
        description: t("Describe your project, and our AI will generate task lists for you. You can edit them later."),
        step: "ai",
      });

    options.push({
      img: "/onboarding/images/UseATemplateIcon.png",
      title: t("Use a Template"),
      description: t("Start quickly with a template: choose a template from our library, and customize it as needed."),
      step: "template",
    });

    options.push({
      img: "/onboarding/images/CreateFromScratchIcon.png",
      title: t("Create From Scratch"),
      description: t("Create a blank project, and add tasks manually. You can make changes at any time."),
      step: "scratch",
    });
    return options;
  }, [createProjectFromPrompt, t]);
  return (
    <div className="lg:tw-max-w-[500px] tw-min-h-[300px]">
      {step === "first" ? (
        <MoveFocusInside disabled={step !== "first"}>
          <div className="tw-flex tw-flex-col tw-mt-2 lg:tw-mt-0 lg:tw-h-full tw-h-[350px] lg:tw-w-full tw-gap-4">
            {createProjectOptions.map((option, index) => (
              <OptionCard
                key={index}
                option={option}
                setStep={setStep}
              />
            ))}
          </div>
        </MoveFocusInside>
      ) : null}
      {step === "ai" ? (
        <MoveFocusInside disabled={step !== "ai"}>
          <div className="tw-mt-2 lg:tw-mt-0 tw-min-w-[280px] tw-w-full tw-flex tw-flex-col tw-gap-4 lg:tw-w-[500px]">
            <ProjectPrompt
              onClose={onClose}
              isAlreadyCompleted={isAlreadyCompleted}
              setProjectId={setProjectId}
            />
          </div>
        </MoveFocusInside>
      ) : null}
      {step === "template" ? (
        <MoveFocusInside disabled={step !== "create-from-ai"}>
          <div className="tw-mt-2 lg:tw-mt-0 tw-min-w-[280px] tw-w-full lg:tw-w-[500px]">
            <ProjectTemplates
              onTemplateClick={onTemplateClick}
              multiAssigneesEnabled={multiAssigneesEnabled}
            />
          </div>
        </MoveFocusInside>
      ) : null}
      {step === "project-form-from-template" ? (
        <MoveFocusInside disabled={step !== "create-from-ai"}>
          <div className="tw-mt-2 lg:tw-mt-0 tw-min-w-[280px] tw-w-full lg:tw-w-[500px]">
            <ProjectForm
              taskLists={initialProjectFormData?.task_lists}
              projectName={initialProjectFormData?.name}
              onClose={onClose}
              setFormState={setFormState}
              isAlreadyCompleted={isAlreadyCompleted}
              setProjectId={setProjectId}
            ></ProjectForm>
          </div>
        </MoveFocusInside>
      ) : null}
      {step === "scratch" ? (
        <MoveFocusInside disabled={step !== "create-from-ai"}>
          <div className="tw-mt-2 lg:tw-mt-0 tw-min-w-[280px] tw-w-full lg:tw-w-[500px]">
            <ProjectForm
              taskLists={[
                {
                  name: "First Task List",
                  id: v4(),
                  tasks: [
                    {
                      name: "First task",
                      id: v4(),
                      variant: SUGGESTION,
                      estimate: "8",
                      job_type_id: defaultJobType?.id,
                      assignee_id: multiAssigneesEnabled ? undefined : loggedUserId,
                      start_on: today.unix(),
                      due_on: today.unix(),
                      assignee_ids: multiAssigneesEnabled ? [loggedUserId] : undefined,
                    },
                  ],
                },
              ]}
              onClose={onClose}
              setFormState={setFormState}
              isAlreadyCompleted={isAlreadyCompleted}
              setProjectId={setProjectId}
            ></ProjectForm>
          </div>
        </MoveFocusInside>
      ) : null}
    </div>
  );
};

LeftSideContent.propTypes = {
  step: PropTypes.string,
  setStep: PropTypes.func,
  onClose: PropTypes.func,
  setFormState: PropTypes.func,
  isAlreadyCompleted: PropTypes.bool,
  setProjectId: PropTypes.func,
};
